import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shigo-ui/ui';

import { dashboardT, type DashboardMessageKey } from '../../../../../lib/messages';
import { RewriteOperator } from '../../model';
import { emptyCondition, needsRewriteValue, type RewriteFormValue } from '../../rewriteForm';
import { rewriteFields } from '../../rewriteFields';

const operatorLabelKeys: Record<RewriteOperator, DashboardMessageKey> = {
  [RewriteOperator.Equals]: 'lists.rewrite.operators.equals',
  [RewriteOperator.NotEquals]: 'lists.rewrite.operators.notEquals',
  [RewriteOperator.GreaterThan]: 'lists.rewrite.operators.greaterThan',
  [RewriteOperator.GreaterThanOrEquals]: 'lists.rewrite.operators.greaterThanOrEquals',
  [RewriteOperator.LessThan]: 'lists.rewrite.operators.lessThan',
  [RewriteOperator.LessThanOrEquals]: 'lists.rewrite.operators.lessThanOrEquals',
  [RewriteOperator.Between]: 'lists.rewrite.operators.between',
  [RewriteOperator.Exists]: 'lists.rewrite.operators.exists',
  [RewriteOperator.NotExists]: 'lists.rewrite.operators.notExists',
  [RewriteOperator.Contains]: 'lists.rewrite.operators.contains',
  [RewriteOperator.In]: 'lists.rewrite.operators.in',
};

export function RewriteConditionGroup({
  ruleIndex,
  groupIndex,
  onRemove,
}: {
  ruleIndex: number;
  groupIndex: number;
  onRemove: () => void;
}) {
  const form = useFormContext<RewriteFormValue>();
  const ruleKey = ruleIndex.toString();
  const groupKey = groupIndex.toString();
  const name =
    `rules.${ruleKey}.condGroups.${groupKey}.conditions` as `rules.${number}.condGroups.${number}.conditions`;
  const conditions = useFieldArray({ control: form.control, name });
  return (
    <section className="border-input overflow-hidden rounded-md border">
      <header className="bg-muted/20 flex items-center justify-between gap-3 px-3 py-2">
        <p className="text-sm font-medium">
          {groupIndex ? dashboardT('lists.rewrite.orGroup') : dashboardT('lists.rewrite.when')}
        </p>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-8 w-8"
          onClick={onRemove}
          aria-label={dashboardT('lists.rewrite.removeGroup')}
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
        </Button>
      </header>
      <div className="space-y-3 px-3 py-3">
        {conditions.fields.map((condition, conditionIndex) => {
          const conditionKey = conditionIndex.toString();
          const prefix =
            `${name}.${conditionKey}` as `rules.${number}.condGroups.${number}.conditions.${number}`;
          const operator = form.watch(`${prefix}.op`);
          return (
            <div
              key={condition.id}
              className="grid min-w-0 grid-cols-[minmax(0,1fr)_36px] gap-3 lg:grid-cols-[1.4fr_1fr_1fr_1fr_36px]"
            >
              <Controller
                control={form.control}
                name={`${prefix}.path`}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger
                      className="col-span-2 h-9 lg:col-span-1"
                      aria-label={dashboardT('lists.rewrite.field')}
                    >
                      <SelectValue placeholder={dashboardT('lists.rewrite.field')} />
                    </SelectTrigger>
                    <SelectContent>
                      {rewriteFields.map((value) => (
                        <SelectItem key={value} value={value}>
                          {value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              <Controller
                control={form.control}
                name={`${prefix}.op`}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={(value: RewriteOperator) => {
                      field.onChange(value);
                      if (!needsRewriteValue(value)) form.setValue(`${prefix}.val`, '');
                      if (value !== RewriteOperator.Between) form.setValue(`${prefix}.val2`, '');
                    }}
                  >
                    <SelectTrigger
                      className="col-span-2 h-9 lg:col-span-1"
                      aria-label={dashboardT('lists.rewrite.operator')}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(RewriteOperator).map((value) => (
                        <SelectItem key={value} value={value}>
                          {dashboardT(operatorLabelKeys[value])}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {needsRewriteValue(operator) ? (
                <Input
                  className="col-span-2 h-9 lg:col-span-1"
                  {...form.register(`${prefix}.val`)}
                  placeholder={dashboardT('lists.rewrite.value')}
                />
              ) : (
                <span className="hidden lg:block" />
              )}
              {operator === RewriteOperator.Between ? (
                <Input
                  className="col-span-2 h-9 lg:col-span-1"
                  {...form.register(`${prefix}.val2`)}
                  placeholder={dashboardT('lists.rewrite.secondValue')}
                />
              ) : (
                <span className="hidden lg:block" />
              )}
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="col-start-2 h-9 w-9 lg:col-start-auto"
                disabled={conditions.fields.length === 1}
                onClick={() => {
                  conditions.remove(conditionIndex);
                }}
                aria-label={dashboardT('lists.rewrite.removeCondition')}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          );
        })}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            conditions.append(emptyCondition());
          }}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {dashboardT('lists.rewrite.addAnd')}
        </Button>
      </div>
    </section>
  );
}
